import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  ChevronRight, ArrowLeft, ExternalLink, QrCode, Wallet, Download, Loader2, Upload,
} from "lucide-react";
import { api } from "@/lib/api";

/**
 * Reusable payment-methods picker dialog for FIRST-TIME payments (not renewal).
 *
 * Shows all enabled payment options — PayMongo (if the super admin has both
 * enabled it AND supplied a link) plus every Custom Payment System (GCash /
 * Maya / bank QR + link + instructions). Tapping a card expands it into a
 * detail view with the QR image, account details, link, instructions, and a
 * **Download QR** button. Manual systems also expose an "I've paid — Upload
 * Receipt" CTA that calls `onUploadReceipt` so the parent can open its
 * existing receipt-upload dialog.
 *
 * Props:
 *  - open (bool), onClose (fn)
 *  - onUploadReceipt (fn) — called when the student taps the upload CTA.
 *  - title (string, optional) — dialog title. Default "Choose payment method".
 */
export default function PaymentMethodsPicker({
  open, onClose, onUploadReceipt, title = "Choose payment method",
}) {
  const [paymongo, setPaymongo] = useState(null);
  const [systems, setSystems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // "paymongo" | system.id

  useEffect(() => {
    if (!open) return;
    setSelected(null);
    setLoading(true);
    Promise.all([
      api.get("/settings/payment/public").catch(() => ({ data: null })),
      api.get("/payment-systems/public").catch(() => ({ data: [] })),
    ]).then(([p, s]) => {
      setPaymongo(p.data || null);
      setSystems(s.data || []);
    }).finally(() => setLoading(false));
  }, [open]);

  const selectedSystem = useMemo(() => {
    if (!selected || selected === "paymongo") return null;
    return systems.find((s) => s.id === selected) || null;
  }, [selected, systems]);

  const paymongoActive = !!(paymongo?.paymongo_enabled && paymongo?.paymongo_payment_link);

  const downloadQr = () => {
    const url = selectedSystem?.qr_code_data_url;
    if (!url) return;
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(selectedSystem.name || "payment").replace(/\s+/g, "-").toLowerCase()}-qr.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const openUpload = () => {
    onClose();
    onUploadReceipt?.();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="payment-methods-picker">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wallet className="w-5 h-5 text-emerald-600" />
            {title}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center gap-2 text-slate-500 py-8 justify-center">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading payment options…
          </div>
        ) : !selected ? (
          <div className="space-y-3" data-testid="picker-list">
            {!paymongoActive && systems.length === 0 && (
              <Card className="p-4 border-amber-200 bg-amber-50">
                <p className="text-sm text-amber-900">
                  No payment methods are enabled yet. Please contact your administrator.
                </p>
              </Card>
            )}
            {paymongoActive && (
              <button
                type="button"
                onClick={() => setSelected("paymongo")}
                className="w-full text-left"
                data-testid="picker-pick-paymongo"
              >
                <Card className="p-4 border-emerald-200 hover:border-emerald-400 hover:shadow-sm transition-all cursor-pointer bg-emerald-50/40">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-md bg-emerald-600 grid place-items-center">
                      <Wallet className="w-5 h-5 text-white" strokeWidth={2} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-emerald-900">PayMongo (Card / GCash)</div>
                      <div className="text-xs text-slate-600">Instant online checkout.</div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                  </div>
                </Card>
              </button>
            )}
            {systems.map((ps) => (
              <button
                type="button"
                key={ps.id}
                onClick={() => setSelected(ps.id)}
                className="w-full text-left"
                data-testid={`picker-pick-system-${ps.id}`}
              >
                <Card className="p-4 border-slate-200 dark:border-slate-700 hover:border-indigo-400 hover:shadow-sm transition-all cursor-pointer">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-md border border-slate-200 dark:border-slate-700 grid place-items-center bg-white dark:bg-slate-900 overflow-hidden shrink-0">
                      {ps.logo_data_url ? (
                        <img src={ps.logo_data_url} alt={`${ps.name} logo`} className="w-full h-full object-contain" />
                      ) : ps.qr_code_data_url ? (
                        <img src={ps.qr_code_data_url} alt="" className="w-full h-full object-contain" />
                      ) : (
                        <QrCode className="w-5 h-5 text-slate-400" strokeWidth={1.5} />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-900 dark:text-slate-100">{ps.name}</div>
                      {/* Account name/number intentionally hidden here — they
                          are shown on the detail screen after a student picks
                          this method, so the picker stays clean and secure. */}
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                  </div>
                </Card>
              </button>
            ))}
          </div>
        ) : (
          <div className="space-y-3" data-testid="picker-detail">
            <button
              type="button"
              onClick={() => setSelected(null)}
              className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900"
              data-testid="picker-back-btn"
            >
              <ArrowLeft className="w-3 h-3" /> Back to payment methods
            </button>

            {selected === "paymongo" ? (
              <Card className="p-4 border-emerald-200 bg-emerald-50/60">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-md bg-emerald-600 grid place-items-center">
                    <Wallet className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold text-emerald-900">Pay via PayMongo</div>
                    <div className="text-xs text-slate-600">Card, GCash, PayMaya, online banking.</div>
                  </div>
                </div>
                <a
                  href={paymongo.paymongo_payment_link}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="picker-open-paymongo"
                >
                  <Button className="w-full mt-3 bg-emerald-600 hover:bg-emerald-700 text-white">
                    Open PayMongo Checkout <ExternalLink className="w-3.5 h-3.5 ml-2" />
                  </Button>
                </a>
                {paymongo?.payment_instructions && (
                  <p className="text-[12px] text-emerald-900 mt-3 whitespace-pre-line">
                    {paymongo.payment_instructions}
                  </p>
                )}
                {onUploadReceipt && (
                  <Button
                    variant="outline"
                    className="w-full mt-2 border-emerald-300 text-emerald-800 hover:bg-emerald-50"
                    onClick={openUpload}
                    data-testid="picker-upload-after-paymongo"
                  >
                    <Upload className="w-4 h-4 mr-2" /> I've paid — Upload Receipt
                  </Button>
                )}
              </Card>
            ) : selectedSystem ? (
              <Card className="p-4 border-slate-200 dark:border-slate-700">
                <div className="flex items-start gap-3">
                  <div className="w-16 h-16 rounded-md border border-slate-200 dark:border-slate-700 grid place-items-center bg-white dark:bg-slate-900 overflow-hidden shrink-0">
                    {selectedSystem.logo_data_url ? (
                      <img
                        src={selectedSystem.logo_data_url}
                        alt={`${selectedSystem.name} logo`}
                        className="w-full h-full object-contain"
                        data-testid="picker-detail-logo"
                      />
                    ) : (
                      <QrCode className="w-8 h-8 text-slate-400" strokeWidth={1.5} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-900 dark:text-slate-100 text-base">{selectedSystem.name}</div>
                    {(selectedSystem.account_name || selectedSystem.account_number) && (
                      <div className="text-[12px] text-slate-600 dark:text-slate-300 mt-0.5">
                        {selectedSystem.account_name && (<><span className="font-medium">Account:</span> {selectedSystem.account_name}</>)}
                        {selectedSystem.account_name && selectedSystem.account_number && <span className="mx-1 text-slate-400">·</span>}
                        {selectedSystem.account_number && (<span className="font-mono">{selectedSystem.account_number}</span>)}
                      </div>
                    )}
                    <div className="mt-2 flex flex-wrap gap-2">
                      {selectedSystem.qr_code_data_url && (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={downloadQr}
                          className="h-7"
                          data-testid="picker-download-qr"
                        >
                          <Download className="w-3 h-3 mr-1" /> Download QR
                        </Button>
                      )}
                      {selectedSystem.link && (
                        <a
                          href={selectedSystem.link}
                          target="_blank"
                          rel="noreferrer"
                          data-testid="picker-open-link"
                        >
                          <Button size="sm" variant="outline" className="h-7">
                            <ExternalLink className="w-3 h-3 mr-1" /> Open payment link
                          </Button>
                        </a>
                      )}
                    </div>
                  </div>
                </div>

                {/* Prominent QR — full-width panel when a QR code is available */}
                {selectedSystem.qr_code_data_url && (
                  <div className="mt-4 flex flex-col items-center bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md p-4" data-testid="picker-detail-qr-panel">
                    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-2 flex items-center gap-1">
                      <QrCode className="w-3 h-3" /> Scan to pay
                    </div>
                    <img
                      src={selectedSystem.qr_code_data_url}
                      alt={`${selectedSystem.name} QR code`}
                      className="w-56 h-56 sm:w-64 sm:h-64 object-contain"
                      data-testid="picker-detail-qr"
                    />
                  </div>
                )}

                {selectedSystem.instructions && (
                  <div className="text-[12px] text-slate-700 dark:text-slate-200 mt-3 whitespace-pre-line bg-slate-50 dark:bg-slate-800/40 p-3 rounded border border-slate-200 dark:border-slate-700">
                    {selectedSystem.instructions}
                  </div>
                )}
                {onUploadReceipt && (
                  <Button
                    className="w-full mt-3 bg-[#1E3A8A] hover:bg-[#1E40AF] text-white"
                    onClick={openUpload}
                    data-testid="picker-upload-after-manual"
                  >
                    <Upload className="w-4 h-4 mr-2" /> I've paid — Upload Receipt
                  </Button>
                )}
              </Card>
            ) : null}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
